import { COLORS } from './graphConfig';
import { truncateLabel, isInViewport } from './graphUtils';
import { LOD_TIER } from './useAdaptiveRendering';
import type { BBox, LodTier, NodeId } from '../../types/graph';

interface RenderNodeData {
  x?: number | null;
  y?: number | null;
  id: NodeId;
  [key: string]: any;
}

interface RenderNodeOptions {
  lodTier: LodTier;
  isHighlighted: boolean;
  hasHover: boolean;
  isHovered: boolean;
  visibleLabels: Set<NodeId> | null;
  color: string;
  radius: number;
  label: string | null;
  bbox?: BBox | null;
  isCenter?: boolean;
  isKeyCollaborator?: boolean;
  showLabelsInOverview?: boolean;
  fontSize?: number;
}

interface RenderUniversityNodeData {
  x?: number | null;
  y?: number | null;
  abbreviation: string;
  peopleCount: number;
  [key: string]: any;
}

interface RenderUniversityNodeOptions {
  isHighlighted: boolean;
  hasHover: boolean;
  isHovered: boolean;
  color: string;
  radius: number;
}

/**
 * Draw a node with LOD-aware rendering.
 */
export function renderNode(node: RenderNodeData, ctx: CanvasRenderingContext2D, opts: RenderNodeOptions): void {
  if (node.x == null || node.y == null) return;

  const {
    lodTier,
    isHighlighted,
    hasHover,
    isHovered,
    visibleLabels,
    color,
    radius,
    label,
    bbox,
    isCenter,
    isKeyCollaborator,
  } = opts;

  // Viewport culling
  if (bbox && !isInViewport(node as any, bbox, radius + 100)) return;

  // Opacity based on hover highlight
  const dimmed = hasHover && !isHighlighted;
  const nodeAlpha = dimmed ? 0.15 : 1.0;

  ctx.save();
  ctx.globalAlpha = nodeAlpha;

  // Pulsing radial glow for center node
  if (isCenter) {
    const pulse = 0.4 + 0.3 * Math.sin(Date.now() / 600);
    const grad = ctx.createRadialGradient(node.x!, node.y!, radius, node.x!, node.y!, radius * 2.5);
    grad.addColorStop(0, `rgba(255, 0, 0, ${(pulse * 0.4).toFixed(3)})`);
    grad.addColorStop(1, 'rgba(255, 0, 0, 0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(node.x!, node.y!, radius * 2.5, 0, 2 * Math.PI);
    ctx.fill();
  }

  // Glow ring for hovered node
  if (isHovered) {
    ctx.shadowColor = color;
    ctx.shadowBlur = 12;
  }

  // Draw circle
  ctx.beginPath();
  ctx.arc(node.x!, node.y!, radius, 0, 2 * Math.PI, false);
  ctx.fillStyle = color;
  ctx.fill();

  ctx.strokeStyle = isKeyCollaborator && !isCenter ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.9)';
  ctx.lineWidth = isKeyCollaborator && !isCenter ? 2 : (isCenter ? 3 : 1.5);
  ctx.stroke();

  // Reset shadow
  if (isHovered) {
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
  }

  // Key collaborator star badge
  if (isKeyCollaborator && !isCenter) {
    drawStarBadge(ctx, node.x! + radius * 0.7, node.y! - radius * 0.7, radius * 0.4);
  }

  // Label rendering based on LOD
  const shouldShowLabel = (() => {
    if (isHovered) return true;
    if (isCenter) return false;
    if (lodTier === LOD_TIER.OVERVIEW && !opts.showLabelsInOverview) return false;
    if (lodTier === LOD_TIER.DETAIL) return true;
    if (visibleLabels === null) return true;
    return visibleLabels.has(node.id);
  })();

  if (shouldShowLabel && label) {
    const fontSize = opts.fontSize || (lodTier === LOD_TIER.DETAIL ? 4 : 3.5);
    const displayLabel = truncateLabel(label, 22);

    ctx.font = `${fontSize}px Sans-Serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const yOffset = radius + fontSize + 2;

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.lineWidth = 2.5;
    ctx.lineJoin = 'round';
    ctx.strokeText(displayLabel, node.x!, node.y! + yOffset);

    ctx.fillStyle = COLORS.textSecondary;
    ctx.fillText(displayLabel, node.x!, node.y! + yOffset);
  }

  ctx.restore();
}

function drawStarBadge(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number): void {
  const spikes = 5;
  const outerR = size;
  const innerR = size * 0.45;
  ctx.save();
  ctx.beginPath();
  for (let i = 0; i < spikes * 2; i++) {
    const r = i % 2 === 0 ? outerR : innerR;
    const angle = (Math.PI / 2 * 3) + (i * Math.PI / spikes);
    const x = cx + Math.cos(angle) * r;
    const y = cy + Math.sin(angle) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fillStyle = '#F59E0B';
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.lineWidth = 0.8;
  ctx.stroke();
  ctx.restore();
}

/**
 * Compute node radius using log-based sizing for more even distribution.
 */
export function getNodeRadius(count: number, base = 3, multiplier = 2): number {
  return Math.log(Math.max(count, 1) + 1) * multiplier + base;
}

/**
 * Draw a university-level node (larger, with abbreviation inside and count below).
 */
export function renderUniversityNode(node: RenderUniversityNodeData, ctx: CanvasRenderingContext2D, opts: RenderUniversityNodeOptions): void {
  if (node.x == null || node.y == null) return;

  const { isHighlighted, hasHover, isHovered, color, radius } = opts;

  const dimmed = hasHover && !isHighlighted;
  const nodeAlpha = dimmed ? 0.15 : 1.0;

  ctx.save();
  ctx.globalAlpha = nodeAlpha;

  // Subtle drop shadow on all university nodes
  ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
  ctx.shadowBlur = 6;
  ctx.shadowOffsetX = 1;
  ctx.shadowOffsetY = 2;

  if (isHovered) {
    ctx.shadowColor = color;
    ctx.shadowBlur = 12;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
  }

  ctx.beginPath();
  ctx.arc(node.x!, node.y!, radius, 0, 2 * Math.PI, false);
  ctx.fillStyle = color;
  ctx.fill();

  // Reset shadow before stroke and text
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;

  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.lineWidth = 2.5;
  ctx.stroke();

  // Abbreviation inside the node
  ctx.font = `bold 6px Sans-Serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#fff';
  ctx.fillText(node.abbreviation, node.x!, node.y!);

  // Count below
  ctx.font = `8px Sans-Serif`;
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
  ctx.lineWidth = 2.5;
  ctx.lineJoin = 'round';
  ctx.strokeText(`${node.peopleCount}`, node.x!, node.y! + radius + 9);
  ctx.fillStyle = COLORS.textSecondary;
  ctx.fillText(`${node.peopleCount}`, node.x!, node.y! + radius + 9);

  ctx.restore();
}
